import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, Row, CardBody, Col, FormGroup, Label, Button, Alert, FormText } from "reactstrap";
import { Redirect } from "react-router-dom";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import { updateFeatureRequest } from "../../redux/featureRequests/action";
import "react-day-picker/lib/style.css";

const FeatureUpdate = ({ match }) => {
  const dispatch = useDispatch();
  const { tableRowResults, created } = useSelector(
    state => state.featureRequests || { tableRowResults: [], created: [] }
  );
  const currentID = match.url.split("/")[2];
  const token = getCookie("token");
  const [listRelease] = useFetch("GET", "releases/?page_size=9999&fields=name,id,official_date", token);
  const [listFeature] = useFetch("GET", "feature-request/", token);
  const [initFormData] = useFetch("GET", `feature-request/${currentID}/`, token);
  const [optionList, setOptionList] = useState([]);

  // ajax handlers
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);
  const [redirect, setRedirect] = useState(false);

  const today = new Date();
  const datePlus = new Date(new Date().setDate(today.getDate() + 11));
  const formattedDate =
    datePlus.getFullYear() +
    "-" +
    ("0" + (datePlus.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + datePlus.getDate()).slice(-2);

  const storeRow = useMemo(() => {
    const rows = [...tableRowResults, ...created];
    return rows.find(i => String(i.id) === String(currentID));
  }, [created, currentID, tableRowResults]);
  const resolvedFormData = storeRow || initFormData;

  useEffect(() => {
    if (!listRelease?.results) {
      return;
    }
    const filtered = listRelease.results.filter(i => i.official_date >= formattedDate);
    const mapped = filtered.map(i => ({ value: i.id, label: i.name }));

    // Keep current release selectable even if it no longer passes date filtering.
    if (
      resolvedFormData?.release &&
      resolvedFormData?.release_name &&
      !mapped.some(i => String(i.value) === String(resolvedFormData.release))
    ) {
      mapped.push({
        value: resolvedFormData.release,
        label: resolvedFormData.release_name,
      });
    }
    setOptionList(mapped);
  }, [formattedDate, listRelease, resolvedFormData]);

  const idList = useMemo(() => {
    const merged = [...(listFeature?.results || []), ...tableRowResults, ...created];
    return merged
      .filter(i => String(i.id) !== String(currentID))
      .map(i => i.release);
  }, [created, currentID, listFeature, tableRowResults]);

  const releaseDefault =
    resolvedFormData?.release && resolvedFormData?.release_name
      ? { value: resolvedFormData.release, label: resolvedFormData.release_name }
      : "";

  return (
    <>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <h3>Edit Marketing Facts</h3>
              <Formik
                enableReinitialize
                initialValues={{
                  release: releaseDefault,
                  dj_tastemaker: resolvedFormData?.dj_tastemaker || "",
                  radio_support: resolvedFormData?.radio_support || "",
                  marketing_pr: resolvedFormData?.marketing_pr || "",
                  label_reach: resolvedFormData?.label_reach || "",
                  artist_remixer: resolvedFormData?.artist_remixer || "",
                  label: resolvedFormData?.label || "",
                  release_name: resolvedFormData?.release_name || "",
                  exclusive_date: resolvedFormData?.exclusive_date || "",
                  release_artist_name: resolvedFormData?.release_artist_name || "",
                }}
                validationSchema={Yup.object().shape({
                  release: Yup.object().required("Existing release is required"),
                  dj_tastemaker: Yup.string(),
                  radio_support: Yup.string(),
                  marketing_pr: Yup.string(),
                  label_reach: Yup.string(),
                  artist_remixer: Yup.string(),
                  label: Yup.string(),
                  release_name: Yup.string(),
                  exclusive_date: Yup.string(),
                  release_artist_name: Yup.string(),
                })}
                onSubmit={(
                  {
                    release,
                    dj_tastemaker,
                    radio_support,
                    marketing_pr,
                    label_reach,
                    artist_remixer,
                    label,
                    release_name,
                    release_artist_name,
                  },
                  { setStatus }
                ) => {
                  setStatus();
                  const body = {
                    id: Number(currentID),
                    release: release?.value || resolvedFormData.release,
                    dj_tastemaker,
                    radio_support,
                    marketing_pr,
                    label_reach,
                    artist_remixer,
                    label,
                    release_name: release?.label || release_name,
                    release_artist_name,
                  };
                  dispatch(updateFeatureRequest(body));
                  setSuccess(true);
                  setTimeout(() => setRedirect(true), 600);
                }}
                render={({ errors, touched, setStatus, setFieldValue, values }) => (
                  <Form className="mt-3" id="updateFeatureForm">
                    <FormGroup row>
                      <Label for="recSelect" sm={3}>
                        Release
                      </Label>
                      <Col sm={9}>
                        <Select
                          components={{ IndicatorSeparator: () => null }}
                          options={optionList}
                          value={values.release}
                          onChange={e => {
                            if (idList.includes(e.value)) {
                              errors.release = "Feature request for this release was already made";
                              setStatus(errors);
                            } else {
                              setFieldValue("release", e);
                            }
                            return errors;
                          }}
                        />
                        {errors.release && (
                          <div className="invalid-feedback" style={{ display: "block" }}>
                            {typeof errors.release === "string" ? errors.release : "Release is required"}
                          </div>
                        )}
                        <FormText>
                          Select a release for which you want to submit featuring request.
                        </FormText>
                      </Col>
                    </FormGroup>

                    <FormGroup row>
                      <Label for="dj_tastemaker" sm={3}>
                        DJ / TasteMaker feedbacks:
                      </Label>
                      <Col sm={9}>
                        <Field
                          name="dj_tastemaker"
                          component="textarea"
                          rows="5"
                          className={"form-control" + (errors.dj_tastemaker && touched.dj_tastemaker ? " is-invalid" : "")}
                        />
                        <ErrorMessage name="dj_tastemaker" component="div" className="invalid-feedback" />
                      </Col>
                    </FormGroup>

                    <FormGroup row>
                      <Label for="radio_support" sm={3}>
                        Radio support
                      </Label>
                      <Col sm={9}>
                        <Field
                          name="radio_support"
                          component="textarea"
                          rows="5"
                          className={"form-control" + (errors.radio_support && touched.radio_support ? " is-invalid" : "")}
                        />
                        <ErrorMessage name="radio_support" component="div" className="invalid-feedback" />
                      </Col>
                    </FormGroup>

                    <FormGroup row>
                      <Label for="marketing_pr" sm={3}>
                        Marketing / Pr
                      </Label>
                      <Col sm={9}>
                        <Field
                          name="marketing_pr"
                          component="textarea"
                          rows="5"
                          className={"form-control" + (errors.marketing_pr && touched.marketing_pr ? " is-invalid" : "")}
                        />
                        <ErrorMessage name="marketing_pr" component="div" className="invalid-feedback" />
                      </Col>
                    </FormGroup>

                    <FormGroup row>
                      <Label for="label_reach" sm={3}>
                        Label and artists reach:
                      </Label>
                      <Col sm={9}>
                        <Field
                          name="label_reach"
                          component="textarea"
                          rows="5"
                          className={"form-control" + (errors.label_reach && touched.label_reach ? " is-invalid" : "")}
                        />
                        <ErrorMessage name="label_reach" component="div" className="invalid-feedback" />
                      </Col>
                    </FormGroup>

                    <FormGroup>
                      <Button type="submit" color="success">
                        Save changes
                      </Button>
                    </FormGroup>
                  </Form>
                )}
              />
              {success && <Alert color="success">Feature request has been updated!</Alert>}
              {redirect ? <Redirect to="/feature" /> : null}
              {errorPut && (
                <Alert color="danger">Something went wrong! Please refresh page and try again!</Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default FeatureUpdate;
